/**
 * This drives CSS/JS-friendly custom file inputs.
 * See SCSS file _custom-file-input.scss.
 * Similar to (& based on) the CF7 version in cf7-file-inputs.ts
 *
 * To create an instance, you can use

	<div data-custom-file-inputs><div>

 * or in TS:

	const customFileInputs = new window["iw"].createCustomFileInputs();
	customFileInputs.appendTo("#some-container");

 *
 * Options can be passed as object to createCustomFileInputs(), or can be
 * specified as data-XXX attributes.
 *
 * Options
 * -----------------------------------------
 * 	min? (int, min no. of files, default 0)
 * 	
 * 	max? (int, max no. of files, default 1)
 * 	
 * 	mimeTypes? (string[], list of allowable mime types,
 * 		if in data-mime-types attr, mime types must be comma-separated)
 *
 *  imagesOnly? (boolean, shortcut for allowing only web-friendly image types; 
 *  overrides 'mimeTypes' option)
 * 	
 * 	maxSize? (int, bytes, max file size per file)
 * 	
 *  onChange? (handler fn which will receive File[], not available for data- attrs)
 */


import { each, escape, find, filter, map, cloneDeep, remove } from "lodash";
import { fileUpload, UploadProgressData, UploadResult } from "./file-upload";

declare const jQuery: any;
const $ = jQuery;

const maxReachedMsg = 'Maximum number of files reached';
const defaultButtonText = "Choose File";

export interface CustomFileInputsOptions
{
	min?: number,
	max?: number,
	mimeTypes?: string[],
	imagesOnly?: boolean,
	maxSize?: number,
	onChange?: (files: File[]) => void,
	buttonText?: string,
	buttonClass?: string,
}

export function initCustomFileInputs(): void
{
	$(`[data-custom-file-inputs]`).each(function () {
		const $wrap = $(this).empty();
		const cfi: CustomFileInputs = createFileInputsFromElement($wrap);
		$wrap.data(`customFileInputs`, cfi);
	});
}

// currentFileProgress & totalProgress are percentages rounded to nearest 1%
export type UploadProgressHandler = (data: UploadProgressData) => void;

function createFileInputsFromElement($wrap): CustomFileInputs
{
	const options = createOptionsFromWrapAttrs($wrap);
	const cfi = new CustomFileInputs(options);
	cfi.appendTo($wrap);
	return cfi;
}

function createOptionsFromWrapAttrs($wrap): CustomFileInputsOptions
{
	const options: CustomFileInputsOptions = {
		min: parseInt($wrap.attr("data-min") || "0", 10) || 0,
		max: parseInt($wrap.attr("data-max") || "1", 10) || 1,
		maxSize: parseInt($wrap.attr("data-max-size") || "0") || 0,
		imagesOnly: $wrap.is("[data-images-only]"),
		buttonText: $wrap.attr("data-button-text") || defaultButtonText,
		buttonClass: $wrap.attr("data-button-class"),
	}
	const mimeTypesString: string = $wrap.attr("data-mime-types");
	options.mimeTypes = mimeTypesString ?
		filter(mimeTypesString.trim().split(/[\s\,]+/g), s => !!s) :
		undefined;
	return options;
}

function getImageMimeTypes(): string[]
{
	return [
		"image/jpeg",
		"image/png",
		"image/gif",
		"image/svg+xml",
	];
}

export class CustomFileInputs
{
	constructor(options: CustomFileInputsOptions)
	{
		options = cloneDeep(options);
		if (!options.buttonText)
		{
			options.buttonText = defaultButtonText;
		}
		options.min = (options.min && options.min >= 0) ? options.min : 0;
		options.max = (options.max && options.max >= 1) ? options.max : 1;
		if (options.max < options.min)
		{
			options.max = options.min;
		}
		this.options = options;
		this.onChange = options.onChange;
		if (options.imagesOnly)
		{
			options.mimeTypes = getImageMimeTypes();
		}
		this.$form = this.createForm(options);
	}

	// uploadHandlerName - corresponds to NAME const on PHP CustomFileUpload subclass
	// Uploads one file at a time in sequence to avoid hitting server limits
	// Returned Promise resolves immediately with no result if there are no files
	// Resolves/rejects with UploadResult object
	public upload(uploadHandlerName: string, uploadData?: any, onProgress?: UploadProgressHandler): Promise<UploadResult>
	{
		const res: any = fileUpload(this.getFiles(), uploadHandlerName, uploadData, onProgress) as any;
		return res as Promise<UploadResult>;
	}

	public clearAll(): void
	{
		each(this.fileInputs, (fileInput: HTMLInputElement) => {
			this.resetFileInput(fileInput);
		});
		this.$list.empty();
		this.updateMaxReachedState();
		this.removedFiles.length = 0;
	}

	// container - jQuery|HTMLElement
	public appendTo(container)
	{
		if (this.appended)
		{
			console.error("CustomFileInputs instance has already been appended");
			return;
		}
		this.appended = true;
		$(container).append(this.$form);
	}

	public maxReached(): boolean
	{
		return this.getNumberOfFiles() >= this.options.max;
	}

	public getNumberOfFiles(): number
	{
		return this.getFiles().length;
	}

	public getFiles(): File[]
	{
		const files: File[] = [];
		const fileStrings: string[] = [];
		each(this.fileInputs, (fileInput: HTMLInputElement) => {
			each(fileInput.files, (file: File) => {
				const fileString: string = getStringRepresentationOfFile(file);
				if (file &&
					!this.fileIsSetForRemoval(file) &&
					(fileStrings.indexOf(fileString) < 0))
				{
					files.push(file);
					fileStrings.push(fileString);
				}
			});
		});
		return files;
	}

	private onChange: (files: File[]) => void;
	private appended = false;
	private options: CustomFileInputsOptions;
	private $form: any; // jQuery
	private fileInputs: HTMLInputElement[] = [];
	private $inputsWrap: any; // jQuery
	private $list: any; // jQuery
	private $maxReached: any; // jQuery
	private $addFileInputBtn: any; // jQuery

	private createForm(options: CustomFileInputsOptions)
	{
		const $wrap = $(`<div class="custom-file-inputs">`);
		this.$inputsWrap = $(`<div style="display: none; ">`).appendTo($wrap);
		this.$list = $(`<div class="custom-file-list">`).appendTo($wrap);
		this.$maxReached = (options.max <= 1) ? $() : $(`<div class="custom-file-list-max-reached">${maxReachedMsg}</div>`).appendTo($wrap);
		this.$addFileInputBtn = this.createAddFileButton().appendTo($wrap);
		this.updateMaxReachedState();
		return $wrap;
	}

	private numFilesWhenLastClicked: number = 0;

	private createAddFileButton()
	{
		const options: CustomFileInputsOptions = this.options;
		const btnText: string = options.buttonText;
		let cls: string = `button custom-file-button`;
		if (options.buttonClass)
		{
			cls += ` ${options.buttonClass}`;
		}
		const $btn = $(`<div class="${cls}">${btnText}</div>`);
		$btn.on("click", (event) => {
			if ($btn.hasClass("disabled"))
			{
				return;
			}
			if (this.maxReached())
			{
				$btn.addClass("disabled");
				alert("Max no. of files reached!");
				console.error("Max reached but add button enabled");
				return;
			}
			this.numFilesWhenLastClicked = this.getNumberOfFiles();
			const fileInput = this.getEmptyFileInput();
			if (fileInput)
			{
				$(fileInput).trigger("click");
			}
			else
			{
				throw new Error(`No empty file input found but maxReached() returned false`);
			}
		});
		return $btn;
	}

	private getEmptyFileInput(): HTMLInputElement|undefined
	{
		const input = find(this.fileInputs, (fileInput: HTMLInputElement) => fileInput.files.length < 1);
		if (input)
		{
			return input;
		}
		return this.addNewFileInput();
	}

	private updateMaxReachedState(): void
	{
		const options: CustomFileInputsOptions = this.options;
		if (this.getNumberOfFiles() >= options.max)
		{
			this.$addFileInputBtn.addClass("disabled");
			this.$maxReached.addClass("show");
		}
		else
		{
			this.$addFileInputBtn.removeClass("disabled");
			this.$maxReached.removeClass("show");
		}
	}

	private addNewFileInput(): HTMLInputElement
	{
		const maxNumFiles: number = this.options.max || 1;
		const $fileInput = $(`<input type="file" ` + (maxNumFiles > 1 ? `multiple="" ` : ``) + `/>`).appendTo(this.$inputsWrap);
		const fileInput: HTMLInputElement = $fileInput[0];
		this.fileInputs.push(fileInput);
		$fileInput.on("change", () => {
			if ((this.numFilesWhenLastClicked + fileInput.files.length) > maxNumFiles)
			{
				alert(`Too many files - a maximum of ${maxNumFiles} is allowed.`);
				this.resetFileInput(fileInput);
				return false;
			}
			if (fileInput.files.length === 0)
			{
				this.resetFileInput(fileInput);
				return;
			}
			let fileDataItems: File[] = [];
			let error: boolean = false;
			each(fileInput.files, (fileData: File) => {
				if (!this.fileTypeIsOk(fileData.type))
				{
					error = true;
					alert("File type is not allowed. Allowed file types are: " + this.getAllowedFileTypesAsString());// FIXME - do something better than alert()
					return false;
				}
				fileDataItems.push(fileData);
			});
			if (error)
			{
				this.resetFileInput(fileInput);
			}
			else
			{
				each(fileDataItems, (fileData: File) => {
					this.unsetFileForRemoval(fileData);
					this.updateDisplayedList();
				});
			}
			this.updateMaxReachedState();
			if (this.onChange)
			{
				this.onChange(this.getFiles());
			}
		});
		return fileInput;
	}

	private fileTypeIsOk(mimeType: string): boolean
	{
		const options: CustomFileInputsOptions = this.options;
		if (!options.mimeTypes || !options.mimeTypes.length)
		{
			return true;
		}
		return options.mimeTypes.indexOf(mimeType) >= 0;
	}

	private getAllowedFileTypesAsString(): string
	{
		const options: CustomFileInputsOptions = this.options;
		if (!options.mimeTypes || !options.mimeTypes.length)
		{
			return "";
		}
		const types: string[] = map(options.mimeTypes, (mime: string) => {
			const type: string = mime.split(/\//)[1];
			if (type === "svg+xml")
			{
				return "svg";
			}
			return type || mime;
		});
		return types.join(", ");
	}

	private updateDisplayedList(): void
	{
		this.$list.empty();
		each(this.getFiles(), (fileData: File) => {
			this.addDisplayListItem(fileData);
		});
	}

	private addDisplayListItem(fileData: File): void
	{
		const $list = this.$list;
		const name: string = escape(fileData.name);
		const $item = $(`<div class="custom-file-list-item"><div>${name}</div></div>`);
		$list.append($item);
		$(`<div class="custom-file-list-item-remove" title="Click to remove file"></div>`).
			appendTo($item).
			on("click", () => {
				$item.remove();
				this.setFileForRemoval(fileData);
				this.updateMaxReachedState();
				if (this.getNumberOfFiles() === 0)
				{
					this.clearAll();
				}
			});
	}

	private removedFiles: string[] = []; // string representations of files

	private fileIsSetForRemoval(file: File): boolean
	{
		const fileAsString: string = getStringRepresentationOfFile(file);
		return !!find(this.removedFiles, (f: string): boolean => f === fileAsString);
	}

	private setFileForRemoval(file: File): void
	{
		this.removedFiles.push(getStringRepresentationOfFile(file));
	}

	private unsetFileForRemoval(file: File): void
	{
		const fileAsString: string = getStringRepresentationOfFile(file);
		remove(this.removedFiles, (f: string): boolean => f === fileAsString);
	}

	private resetFileInput(fileInput: HTMLInputElement) {
		fileInput.value = "";
		fileInput.type = "";
		fileInput.type = "file";
	}
}

function createCustomFileInputs(options: CustomFileInputsOptions): CustomFileInputs
{
	return new CustomFileInputs(options);
}

function getStringRepresentationOfFile(file: File): string
{
	return JSON.stringify({
		s: file.size,
		n: file.name,
		m: file.lastModified,
		t: file.type,
	})
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].createCustomFileInputs = createCustomFileInputs;
